/* © 2017 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, January 01, 2017
 * For information or permission request, email info@aetonixsystems.com
 */

import PropTypes from "prop-types";
import React, { Component } from "react";
import { View, StyleSheet, Dimensions } from "react-native";
import memoize from "memoizee";
import validator from "is-my-json-valid";
import FormInput from "./FormInput";
import IncrementalButtons from "./IncrementalButtons";
import SubmitActions from "../SubmitActions";
import StyledText from "ae-base/Base/StyledText";
import StyledIcon from "ae-base/Base/StyledIcon";
import StyledButton from "ae-base/Base/StyledButton";

import Style from "ae-style";
import xtend from "xtend";

import { useResources } from "@aetonix/hooks";

const makeValidator = memoize(validator);

const VALIDATOR_OPTIONS = {
	verbose: true,
	formats: {
		email: /^[^ ]+@[^ ]+$/
	}
};

const styles = StyleSheet.create({
	button: {
		alignItems: "center",
		borderRadius: 5,
		width: 50
	},
	buttonsContainer: {
		alignItems: "center",
		flexDirection: "row",
		justifyContent: "space-between"
	},
	containerStyle: {
		marginVertical: Style.marginContentSmall
	},
	errorBoxContainer: {
		flexDirection: "row"
	},
	errorContainer: {
		margin: Style.layout.marginSlim
	},
	errorMapContainer: {
		flexDirection: "row",
		flexWrap: "wrap"
	},
	errorsContainer: {
		flex: 1,
		flexWrap: "wrap"
	},
	icon: {
		marginRight: Style.layout.marginLarge
	}
});

class IncrementalFormComp extends Component {
	constructor(props) {
		super(props);
		this.state = {
			number: 0,
			changeMap: {}
		};
		this.setRef = setRef.bind(this);
		this.handleChange = handleChange.bind(this);
		this.handleUpdate = handleUpdate.bind(this);
		this.jumpTo = jumpTo.bind(this);
	}

	render() {
		const props = this.props;
		const localize = props.localize;
		const schema = props.schema;
		const defaultValue = props.defaultValue;
		const contrast = props.contrast;
		const errors = props.errors;
		const submit = props.onSubmit;
		const submitText = props.submitText;
		const cancelText = props.cancelText;
		const submitStyle = props.submitStyle;
		const textStyle = props.textStyle;
		const onCancel = props.onCancel;
		const onChangePage = props.onChangePage;
		const number = this.state.number;
		const changeMap = this.state.changeMap;
		const showCustomAddButton = props.showCustomAddButton;

		const keys = Object.keys(schema.properties);
		const max = keys.length - 1;

		const displayProperty = keys[number];

		const propertySchema = schema.properties[displayProperty];
		const propertyErrors = errors[displayProperty];
		const propertyDefaultValue = defaultValue[displayProperty];

		const currentPage = localize("incrementalform.page", {
			current: number + 1,
			max: max + 1
		});

		const containerStyle = [styles.containerStyle];

		if(props.platform !== "dashboards") {
			containerStyle.push({
				width: Dimensions.get("window").width / 1.3
			});
		}

		const isRequired = (schema.required || []).indexOf(displayProperty) !== -1;
		const canNext = changeMap[displayProperty] || !isRequired;
		const submitAction = props.subForm ? null : (
			<SubmitActions
				onSubmit={submit}
				submitText={submitText}
				cancelText={cancelText}
				onCancel={onCancel}
				submitStyle={submitStyle}
				textStyle={textStyle}
				errors={errors}
			/>
		);
		const dataId = props.dataId;

		return (
			<View style={containerStyle}>
				<FormInput
					key={displayProperty}
					schema={propertySchema}
					defaultValue={propertyDefaultValue}
					onChange={this.handleUpdate}
					onSubmit={submit}
					name={displayProperty}
					contrast={contrast}
					dataId={dataId}
					errors={propertyErrors}
					platform={props.platform}
					unsupportedFeatures={props.unsupportedFeatures}
					showCustomAddButton = {showCustomAddButton}
				/>
				<View style={styles.buttonsContainer}>
					<StyledText>{currentPage}</StyledText>
					<IncrementalButtons
						key={"buttons_" + max}
						schema={propertySchema}
						max={max}
						changed={canNext}
						onChange={this.handleChange}
						onChangePage={onChangePage}
						ref={this.setRef}
					/>
				</View>
				{submitAction}
				<ErrorBox errors={errors} keys={keys} jumpTo={this.jumpTo} />
			</View>
		);
	}
}

function handleUpdate(value) {
	const schema = this.props.schema;
	const keys = Object.keys(schema.properties);
	const displayProperty = keys[this.state.number];
	const changeMap = this.state.changeMap;
	this.props.onChange(displayProperty)(value);

	const propertySchema = schema.properties[displayProperty];
	const validate = makeValidator(propertySchema, VALIDATOR_OPTIONS);
	validate(value);
	const error = validate.errors;
	const update = {};

	if (!error) {
		update[displayProperty] = true;
	}
	this.setState({
		changeMap: xtend(changeMap, update)
	});
}
function handleChange(number) {
	this.setState({
		number: number
	});
}

function countErrors(errors) {
	return Object.keys(errors).reduce(function (acc, errorKey) {
		acc[errorKey] = errors[errorKey].length;
		return acc;
	}, {});
}

function ErrorBox(props) {
	const { localize } = useResources();
	const errors = props.errors;
	const keys = props.keys;
	const jumpToPage = props.jumpTo;

	if (Array.isArray(errors) && errors.length === 0) return null;
	else if (typeof errors === "object" && Object.keys(errors).length === 0) return null;

	const errorsCount = countErrors(errors);

	const errorsMap = keys.map(function (key, index) {
		if (!errorsCount[key]) return null;
		const message = "" + (index + 1);
		const onPress = function () {
			jumpToPage(index);
		};
		return (
			<View key={message} style={styles.errorContainer}>
				<StyledButton
					id={props.id + "_error_" + message}
					style={styles.button}
					key={message}
					title={message}
					onPress={onPress}
					alert
					standard
					centered
				/>
			</View>
		);
	});

	return (
		<View style={styles.errorBoxContainer}>
			<StyledIcon style={styles.icon} name="alert" smallHeading alert />
			<View style={styles.errorsContainer}>
				<StyledText>{localize("incrementalform.erroroccured")}</StyledText>
				<View style={styles.errorMapContainer}>{errorsMap}</View>
			</View>
		</View>
	);
}

function setRef(ref) {
	this.buttons = ref;
}

function jumpTo(page) {
	this.buttons.jumpTo(page);
}

export default function IncrementalForm(props){
	const { localize } = useResources();
	return <IncrementalFormComp {...props} localize={localize} />;
}